import { useEffect, useRef as useReactRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiDownload } from '../api/client';
import { useFormDraft } from '../api/drafts';
import { useRef } from '../api/hooks';
import type { Quote } from '../api/types';
import { fmtMoney } from '../lib/format';

/* Proposal builder for Tricord Analytics on ActionStep Legal Accounting.
   Print -> formatted Word download; Finalize -> creates the app quote
   (items resolved in QuickBooks) ready for the approver Submit step. */

interface CustomReportRow {
  description: string;
  cost: string;
  qbItemName: string;
}

interface FormState {
  customerName: string;
  contact: string;
  email: string;
  address: string;
  phone: string;
  users: string;
  tier: 'basic' | 'standard';
  tierMonthly: string;
  tierItemName: string;
  setupFee: string;
  setupItemName: string;
  feeAllocEnabled: boolean;
  feeAllocOneTime: string;
  feeAllocMonthly: string;
  feeAllocOneTimeItem: string;
  feeAllocMonthlyItem: string;
  qstEnabled: boolean;
  qstOneTime: string;
  qstItemName: string;
  fileImportEnabled: boolean;
  fileImportItemName: string;
  vaultEnabled: boolean;
  vaultOneTime: string;
  vaultMonthly: string;
  vaultOneTimeItem: string;
  vaultMonthlyItem: string;
  customReports: CustomReportRow[];
  notes: string;
}

const initialState = (): FormState => ({
  customerName: '', contact: '', email: '', address: '', phone: '',
  users: '10',
  tier: 'basic', tierMonthly: '400', tierItemName: '',
  setupFee: '3500', setupItemName: 'Tricord Analytics Setup Fee',
  feeAllocEnabled: false, feeAllocOneTime: '3000', feeAllocMonthly: '200',
  feeAllocOneTimeItem: 'Tricord Analytics Fee Allocation Module Setup',
  feeAllocMonthlyItem: 'Tricord Analytics Fee Allocation Monthly Fee',
  qstEnabled: false, qstOneTime: '1000', qstItemName: 'Tricord Analytics QST Module',
  fileImportEnabled: false, fileImportItemName: 'Tricord Analytics File Import',
  vaultEnabled: false, vaultOneTime: '13000', vaultMonthly: '300',
  vaultOneTimeItem: 'Tricord Vault Registry Implementation',
  vaultMonthlyItem: 'Tricord Vault Registry Monthly Fee',
  customReports: [],
  notes: '',
});

const n = (s: string): number => (s === '' || Number.isNaN(Number(s)) ? 0 : Number(s));
// A price field is "priced" when it holds an explicit number (0 is allowed; blank/garbage is not).
const priced = (s: string): boolean => s.trim() !== '' && !Number.isNaN(Number(s));
const tierDefault = (tier: 'basic' | 'standard', users: number): number => (tier === 'basic' ? 400 : 600) + (users > 60 ? 100 : 0);

export function ProposalPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const customers = useRef('customers');
  const items = useRef('items');

  const [f, setF] = useState<FormState>(initialState());
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const [downloadErr, setDownloadErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Default tier price: basic 400 / standard 600, +$100 when over 60 users.
  const over60 = n(f.users) > 60;
  const defaultTierMonthly = tierDefault(f.tier, n(f.users));
  const lastDefault = useReactRef(defaultTierMonthly);

  // Autosave draft (server-side, kind 'proposal').
  const { loadFailed, discard, savedAt } = useFormDraft('proposal', f, (p) => {
    const h = { ...initialState(), ...(p as Partial<FormState>) };
    // Re-baseline the tier default against the HYDRATED tier/users so a custom price
    // that happens to equal some other default is never mistaken for "the default".
    lastDefault.current = tierDefault(h.tier, n(h.users));
    setF(h);
  });
  const clearDraft = () => {
    const next = initialState();
    lastDefault.current = tierDefault(next.tier, n(next.users));
    void discard(next);
    setF(next);
    qc.invalidateQueries({ queryKey: ['drafts'] });
  };

  useEffect(() => {
    // Re-apply the default when tier / user-threshold changes, unless the user typed a custom price.
    // Ref read/write hoisted OUT of the updater so it stays pure under StrictMode double-invocation.
    const prevDefault = lastDefault.current;
    lastDefault.current = defaultTierMonthly;
    setF((s) => {
      const wasDefault = s.tierMonthly === '' || Number(s.tierMonthly) === prevDefault;
      return wasDefault && s.tierMonthly !== String(defaultTierMonthly) ? { ...s, tierMonthly: String(defaultTierMonthly) } : s;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTierMonthly]);

  const setCr = (i: number, patch: Partial<CustomReportRow>) =>
    setF((s) => ({ ...s, customReports: s.customReports.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addCr = () =>
    setF((s) => ({ ...s, customReports: [...s.customReports, { description: '', cost: '1000', qbItemName: 'Tricord Analytics Custom Report' }] }));
  const removeCr = (i: number) => setF((s) => ({ ...s, customReports: s.customReports.filter((_, idx) => idx !== i) }));

  const oneTimeTotal =
    n(f.setupFee) +
    (f.feeAllocEnabled ? n(f.feeAllocOneTime) : 0) +
    (f.qstEnabled ? n(f.qstOneTime) : 0) +
    (f.vaultEnabled ? n(f.vaultOneTime) : 0) +
    f.customReports.reduce((s, r) => (r.description.trim() ? s + n(r.cost) : s), 0);
  const monthlyTotal = n(f.tierMonthly) + (f.feeAllocEnabled ? n(f.feeAllocMonthly) : 0) + (f.vaultEnabled ? n(f.vaultMonthly) : 0);

  const payload = () => ({
    customer: {
      name: f.customerName.trim(),
      contact: f.contact.trim() || undefined,
      email: f.email.trim() || undefined,
      address: f.address.trim() || undefined,
      phone: f.phone.trim() || undefined,
    },
    users: Math.max(1, Math.round(n(f.users))),
    tier: f.tier,
    tierMonthly: n(f.tierMonthly),
    tierItemName: f.tierItemName.trim() || undefined,
    setupFee: n(f.setupFee),
    setupItemName: f.setupItemName.trim() || undefined,
    modules: {
      feeAllocation: {
        enabled: f.feeAllocEnabled,
        oneTime: n(f.feeAllocOneTime),
        monthly: n(f.feeAllocMonthly),
        oneTimeItemName: f.feeAllocOneTimeItem.trim() || undefined,
        monthlyItemName: f.feeAllocMonthlyItem.trim() || undefined,
      },
      qst: { enabled: f.qstEnabled, oneTime: n(f.qstOneTime), itemName: f.qstItemName.trim() || undefined },
      fileImport: { enabled: f.fileImportEnabled, itemName: f.fileImportItemName.trim() || undefined },
      vaultRegistry: {
        enabled: f.vaultEnabled,
        oneTime: n(f.vaultOneTime),
        monthly: n(f.vaultMonthly),
        oneTimeItemName: f.vaultOneTimeItem.trim() || undefined,
        monthlyItemName: f.vaultMonthlyItem.trim() || undefined,
      },
    },
    customReports: f.customReports
      .filter((r) => r.description.trim())
      .map((r) => ({ description: r.description.trim(), cost: n(r.cost), qbItemName: r.qbItemName.trim() || undefined })),
    notes: f.notes.trim() || undefined,
  });

  const valid =
    f.customerName.trim().length > 0 &&
    Number.isInteger(Number(f.users)) &&
    n(f.users) > 0 &&
    priced(f.tierMonthly) &&
    priced(f.setupFee) &&
    (!f.feeAllocEnabled || (priced(f.feeAllocOneTime) && priced(f.feeAllocMonthly))) &&
    (!f.qstEnabled || priced(f.qstOneTime)) &&
    (!f.vaultEnabled || (priced(f.vaultOneTime) && priced(f.vaultMonthly))) &&
    f.customReports.every((r) => r.description.trim().length > 0 && priced(r.cost));

  const download = async () => {
    setDownloadErr(null);
    setDownloading(true);
    try {
      await apiDownload('/api/proposals/export', payload());
    } catch (e) {
      setDownloadErr((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const finalize = useMutation({
    mutationFn: () => api.post<{ quote: Quote }>('/api/proposals', payload()),
    onSuccess: (res) => {
      clearDraft();
      qc.invalidateQueries({ queryKey: ['quotes'] });
      navigate(`/quotes/${res.quote.id}`);
    },
  });
  // Synchronous double-click guard (isPending only flips after a re-render).
  const finalizing = useReactRef(false);
  const startFinalize = () => {
    if (finalizing.current) return;
    finalizing.current = true;
    finalize.mutate(undefined, { onSettled: () => { finalizing.current = false; } });
  };

  const itemInput = (label: string, value: string, onChange: (v: string) => void) => (
    <div className="field" style={{ marginBottom: 0 }}>
      <label style={{ fontSize: '0.72rem' }}>{label}</label>
      <input list="proposal-item-list" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );

  return (
    <>
      <h1>Proposal — ActionStep Legal Accounting</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        Build a Tricord Analytics quote/proposal. Print exports the formatted Word document; Finalize creates the quote (items in
        QuickBooks) ready for Submit.
      </p>

      <datalist id="proposal-customer-list">{customers.data?.map((c) => <option key={c.id} value={c.name} />)}</datalist>
      <datalist id="proposal-item-list">{items.data?.map((it) => <option key={it.id} value={it.name} />)}</datalist>

      {downloadErr && <div className="banner error">{downloadErr}</div>}
      {finalize.error && <div className="banner error">{(finalize.error as Error).message}</div>}
      {loadFailed && <div className="banner warn">Your saved draft couldn't be loaded — autosave is paused so it isn't overwritten. Reload the page to retry.</div>}

      <div className="card">
        <h2>Customer</h2>
        <div className="row cols-2">
          <div className="field">
            <label>Customer / firm name *</label>
            <input list="proposal-customer-list" value={f.customerName} onChange={(e) => set('customerName', e.target.value)} placeholder="Existing QuickBooks customer or new" />
          </div>
          <div className="field">
            <label>Contact person</label>
            <input value={f.contact} onChange={(e) => set('contact', e.target.value)} />
          </div>
        </div>
        <div className="row cols-2">
          <div className="field">
            <label>Email</label>
            <input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div className="field">
            <label>Address</label>
            <input value={f.address} onChange={(e) => set('address', e.target.value)} />
          </div>
        </div>
        <div className="row cols-2">
          <div className="field">
            <label>Phone</label>
            <input value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="416 555 1234" />
          </div>
          <div className="field">
            <label>Number of users *</label>
            <input type="number" min="1" step="1" value={f.users} onChange={(e) => set('users', e.target.value)} />
            {over60 && <span className="muted" style={{ fontSize: '0.78rem' }}>Over 60 users — monthly fee +$100 applied.</span>}
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Reporting tier</h2>
        <div className="row cols-2">
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600 }}>
            <input type="radio" name="tier" style={{ width: 'auto' }} checked={f.tier === 'basic'} onChange={() => set('tier', 'basic')} />
            Basic — $400/month
          </label>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600 }}>
            <input type="radio" name="tier" style={{ width: 'auto' }} checked={f.tier === 'standard'} onChange={() => set('tier', 'standard')} />
            Standard Reporting — $600/month
          </label>
        </div>
        <div className="row cols-3" style={{ marginTop: '0.75rem' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Monthly fee (editable)</label>
            <input type="number" min="0" step="0.01" value={f.tierMonthly} onChange={(e) => set('tierMonthly', e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Setup fee (standard $3,500)</label>
            <input type="number" min="0" step="0.01" value={f.setupFee} onChange={(e) => set('setupFee', e.target.value)} />
          </div>
        </div>
        <div className="row cols-2" style={{ marginTop: '0.75rem' }}>
          {itemInput('QuickBooks item — monthly fee', f.tierItemName, (v) => set('tierItemName', v))}
          {itemInput('QuickBooks item — setup', f.setupItemName, (v) => set('setupItemName', v))}
        </div>
      </div>

      <div className="card">
        <h2>Additional modules</h2>

        <div className="quote-line">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600, marginBottom: 0 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={f.feeAllocEnabled} onChange={(e) => set('feeAllocEnabled', e.target.checked)} />
              Fee Allocations &amp; Budgets — $3,000 one-time + $200/month
            </label>
            <a className="btn" href="/api/proposals/onepager/fee-allocation" style={{ fontSize: '0.78rem', padding: '5px 12px' }}>
              📄 Module one-pager (Word)
            </a>
          </div>
          {f.feeAllocEnabled && (
            <p className="muted" style={{ fontSize: '0.78rem', margin: '0.4rem 0 0' }}>
              The module brief is embedded in the proposal document automatically.
            </p>
          )}
          {f.feeAllocEnabled && (
            <>
              <div className="row cols-2" style={{ marginTop: '0.6rem' }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>One-time</label>
                  <input type="number" min="0" step="0.01" value={f.feeAllocOneTime} onChange={(e) => set('feeAllocOneTime', e.target.value)} />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Monthly</label>
                  <input type="number" min="0" step="0.01" value={f.feeAllocMonthly} onChange={(e) => set('feeAllocMonthly', e.target.value)} />
                </div>
              </div>
              <div className="row cols-2" style={{ marginTop: '0.6rem' }}>
                {itemInput('QuickBooks item — setup', f.feeAllocOneTimeItem, (v) => set('feeAllocOneTimeItem', v))}
                {itemInput('QuickBooks item — monthly', f.feeAllocMonthlyItem, (v) => set('feeAllocMonthlyItem', v))}
              </div>
            </>
          )}
        </div>

        <div className="quote-line">
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={f.qstEnabled} onChange={(e) => set('qstEnabled', e.target.checked)} />
            QST reporting (FPZ-500) — $1,000 one-time
          </label>
          {f.qstEnabled && (
            <div className="row cols-2" style={{ marginTop: '0.6rem' }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>One-time</label>
                <input type="number" min="0" step="0.01" value={f.qstOneTime} onChange={(e) => set('qstOneTime', e.target.value)} />
              </div>
              {itemInput('QuickBooks item', f.qstItemName, (v) => set('qstItemName', v))}
            </div>
          )}
        </div>

        <div className="quote-line">
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={f.fileImportEnabled} onChange={(e) => set('fileImportEnabled', e.target.checked)} />
            File Import Manager — free add-on
          </label>
        </div>

        <div className="quote-line">
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={f.vaultEnabled} onChange={(e) => set('vaultEnabled', e.target.checked)} />
            Vault Registry — implementation + monthly hosting
          </label>
          {f.vaultEnabled && (
            <>
              <div className="row cols-2" style={{ marginTop: '0.6rem' }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>One-time (implementation &amp; migration)</label>
                  <input type="number" min="0" step="0.01" value={f.vaultOneTime} onChange={(e) => set('vaultOneTime', e.target.value)} />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Monthly (hosting &amp; support)</label>
                  <input type="number" min="0" step="0.01" value={f.vaultMonthly} onChange={(e) => set('vaultMonthly', e.target.value)} />
                </div>
              </div>
              <div className="row cols-2" style={{ marginTop: '0.6rem' }}>
                {itemInput('QuickBooks item — implementation', f.vaultOneTimeItem, (v) => set('vaultOneTimeItem', v))}
                {itemInput('QuickBooks item — monthly', f.vaultMonthlyItem, (v) => set('vaultMonthlyItem', v))}
              </div>
            </>
          )}
        </div>

        <div className="quote-line">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Custom reports — $1,000 each</strong>
            <button type="button" onClick={addCr}>+ Add custom report</button>
          </div>
          {f.customReports.map((r, i) => (
            <div className="row cols-3" style={{ marginTop: '0.6rem', alignItems: 'end' }} key={i}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Description *</label>
                <input value={r.description} onChange={(e) => setCr(i, { description: e.target.value })} placeholder="e.g. Client WIP Referral report" />
                {!r.description.trim() && <span className="error-text" style={{ fontSize: '0.75rem' }}>Description required (or remove this row)</span>}
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Cost</label>
                <input type="number" min="0" step="0.01" value={r.cost} onChange={(e) => setCr(i, { cost: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>{itemInput('QuickBooks item', r.qbItemName, (v) => setCr(i, { qbItemName: v }))}</div>
                <button type="button" className="link" onClick={() => removeCr(i)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="row cols-2">
          <div className="field">
            <label>Notes (appear on the proposal)</label>
            <textarea rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
          <div className="right" style={{ alignSelf: 'end', lineHeight: 1.9 }}>
            <div className="muted">One-time total: <strong>{fmtMoney(oneTimeTotal)}</strong></div>
            <div style={{ fontSize: '1.05rem' }}>
              <strong>Monthly: {fmtMoney(monthlyTotal)} / month</strong>
            </div>
            <div className="muted" style={{ fontSize: '0.78rem' }}>CAD, plus applicable taxes</div>
          </div>
        </div>
      </div>

      <div className="btn-row">
        <button type="button" onClick={download} disabled={!valid || downloading}>
          {downloading ? 'Generating…' : '🖨 Print — export Word proposal'}
        </button>
        <button
          type="button"
          className="primary"
          onClick={startFinalize}
          disabled={!valid || finalize.isPending}
        >
          {finalize.isPending ? 'Finalizing…' : 'Finalize — create quote in QuickBooks'}
        </button>
        <button type="button" className="link" onClick={clearDraft}>Discard draft</button>
        <span className="muted">
          Autosaves as you type.{savedAt ? ' · Draft saved' : ''} Finalize creates the items (if new) and a Created quote; pushing the
          estimate is the separate approver Submit step.
        </span>
      </div>
    </>
  );
}
